/**
 * 자막 크기 +/− 스테퍼(§14).
 *
 * 예전의 기본/발표/발표XL 프리셋을 대신한다 — 회의실마다 필요한 크기가
 * 다른데 선택지가 셋뿐이었다.
 *
 * **단축키는 브라우저 확대와 충돌하지 않게 고른다.** Ctrl/Cmd +/− 는
 * 브라우저 줌이라 가로챌 수 없고(가로채면 페이지 전체 확대를 빼앗는다),
 * 맨손 +/− 는 채팅 입력에 글자를 치는 순간 발동한다. 그래서 **Alt(Option)
 * + / −** 를 쓴다: 브라우저·OS 예약이 아니고, 입력 중에도 안전하다.
 * 입력 요소에 포커스가 있으면 그마저도 무시한다.
 */

import { useEffect } from 'react'
import { Button } from '@cupia/ui/button'
import { Minus, Plus, Type } from 'lucide-react'
import { stepLabel } from '../lib/caption-scale'

export interface CaptionSizeStepperProps {
  step: number
  onIncrease: () => void
  onDecrease: () => void
  canIncrease: boolean
  canDecrease: boolean
  /** 좁은 화면에서는 라벨을 줄인다 */
  compact?: boolean
}

/** 입력 중이면 단축키를 삼킨다 — 글자를 치다 화면이 커지면 안 된다. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  )
}

export function CaptionSizeStepper({
  step,
  onIncrease,
  onDecrease,
  canIncrease,
  canDecrease,
  compact = false,
}: CaptionSizeStepperProps): React.ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Ctrl/Cmd 조합은 **건드리지 않는다** — 브라우저 줌의 자리다.
      if (!e.altKey || e.ctrlKey || e.metaKey) return
      if (isTyping(e.target)) return
      // 자판·레이아웃에 따라 '+' 가 '=' 로, '−' 가 '-' 로 온다.
      if (e.key === '+' || e.key === '=' || e.code === 'Equal') {
        e.preventDefault()
        onIncrease()
      } else if (e.key === '-' || e.key === '_' || e.code === 'Minus') {
        e.preventDefault()
        onDecrease()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onIncrease, onDecrease])

  return (
    <div
      // shrink-0 — 좁은 화면에서 이 그룹이 눌리면 +/− 가 화면 밖으로
      // 밀려 **누를 수 없게** 된다(실측 360px 에서 300..460 으로 잘렸다).
      className="flex shrink-0 items-center gap-0.5 sm:gap-1"
      role="group"
      aria-label="자막 크기"
      data-testid="caption-size-stepper"
    >
      {/* 아이콘은 좁은 화면에서 접는다 — 폭을 먹는 장식이 먼저 나간다 */}
      {!compact && (
        <Type
          className="hidden h-4 w-4 text-[var(--color-muted-foreground)] sm:block"
          aria-hidden
        />
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDecrease}
        disabled={!canDecrease}
        aria-label="자막 크기 줄이기 (Alt+-)"
        title="자막 크기 줄이기 (Alt+-)"
        data-testid="caption-size-decrease"
      >
        <Minus className="h-4 w-4" />
      </Button>
      {/* 현재 단계를 **숫자로** 보여 준다 — +/− 만 있으면 끝에 닿았는지,
          지금 어디쯤인지 알 수 없다. 버튼 비활성만으로는 약하다. */}
      <span
        className="min-w-[2.5rem] text-center font-mono text-(length:--text-xs-size) tabular-nums text-[var(--color-muted-foreground)] sm:min-w-[3.25rem]"
        data-testid="caption-size-level"
        aria-live="polite"
        aria-label={`자막 크기 ${stepLabel(step)}`}
      >
        {stepLabel(step)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onIncrease}
        disabled={!canIncrease}
        aria-label="자막 크기 키우기 (Alt++)"
        title="자막 크기 키우기 (Alt++)"
        data-testid="caption-size-increase"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  )
}
